import { useState, useEffect } from 'react';

const HOST = process.env.REACT_APP_GARAGE_ENDPOINT?.replace('https://', '');
const ACCESS_KEY = process.env.REACT_APP_GARAGE_ACCESS_KEY;
const SECRET_KEY = process.env.REACT_APP_GARAGE_SECRET_KEY;
const BUCKET = process.env.REACT_APP_GARAGE_BUCKET;
const REGION = process.env.REACT_APP_GARAGE_REGION || 'garage';
const EXPIRES = 604800; // 7 días (máximo permitido por Garage)

async function hmac(key, message) {
  const keyData = typeof key === 'string' ? new TextEncoder().encode(key) : key;
  const cryptoKey = await crypto.subtle.importKey(
    'raw', keyData, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
  );
  return new Uint8Array(
    await crypto.subtle.sign('HMAC', cryptoKey, new TextEncoder().encode(message))
  );
}

async function sha256(data) {
  const buf = typeof data === 'string' ? new TextEncoder().encode(data) : data;
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', buf)))
    .map(b => b.toString(16).padStart(2, '0')).join('');
}

function toHex(bytes) {
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function getSigningKey(datestamp) {
  const k1 = await hmac(`AWS4${SECRET_KEY}`, datestamp);
  const k2 = await hmac(k1, REGION);
  const k3 = await hmac(k2, 's3');
  return hmac(k3, 'aws4_request');
}

// Genera presigned GET URL válida 7 días para un path de Garage
async function presign(s3Key) {
  const now = new Date();
  const amzdate = now.toISOString().replace(/[:-]|\.\d{3}/g, '').slice(0, 15) + 'Z';
  const datestamp = amzdate.slice(0, 8);
  const credScope = `${datestamp}/${REGION}/s3/aws4_request`;
  const sigKey = await getSigningKey(datestamp);

  const psCredential = encodeURIComponent(`${ACCESS_KEY}/${credScope}`);
  const qs = `X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=${psCredential}&X-Amz-Date=${amzdate}&X-Amz-Expires=${EXPIRES}&X-Amz-SignedHeaders=host`;
  const psCanon = `GET\n/${BUCKET}/${s3Key}\n${qs}\nhost:${HOST}\n\nhost\nUNSIGNED-PAYLOAD`;
  const psSts = `AWS4-HMAC-SHA256\n${amzdate}\n${credScope}\n${await sha256(psCanon)}`;
  const psSig = toHex(await hmac(sigKey, psSts));

  return `https://${HOST}/${BUCKET}/${s3Key}?${qs}&X-Amz-Signature=${psSig}`;
}

// Devuelve URL para mostrar una imagen.
// - Si es una URL completa (Firebase, antigua presignada) → devuelve tal cual
// - Si es un path de Garage (ej: "events/123.png") → genera presigned URL fresca
export async function getGarageUrl(src) {
  if (!src) return '';
  // URLs completas (http, data:, blob:) se devuelven tal cual
  if (src.startsWith('http') || src.startsWith('data:') || src.startsWith('blob:')) return src;
  return presign(src);
}

// Hook de React: resuelve un path a URL presignada
const isDirectUrl = (s) => !s || s.startsWith('http') || s.startsWith('data:') || s.startsWith('blob:');

export function useGarageUrl(src) {
  const [url, setUrl] = useState(() => (isDirectUrl(src) ? src || '' : ''));

  useEffect(() => {
    if (!src) { setUrl(''); return; }
    if (isDirectUrl(src)) { setUrl(src); return; }
    getGarageUrl(src).then(setUrl).catch(() => setUrl(''));
  }, [src]);

  return url;
}

// Hook de React: resuelve un array de paths a URLs presignadas
export function useGarageUrls(srcs) {
  const [urls, setUrls] = useState(() =>
    (srcs || []).map(s => (s?.startsWith('http') ? s : ''))
  );

  useEffect(() => {
    if (!srcs?.length) { setUrls([]); return; }
    Promise.all(srcs.map(s => getGarageUrl(s))).then(setUrls);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(srcs)]);

  return urls;
}

// Sube un archivo a Garage y devuelve el path (ej: "events/1234.png")
// El path se guarda en Firestore; la URL se genera al vuelo con useGarageUrl
export async function uploadToGarage(file, folder = 'uploads') {
  const now = new Date();
  const amzdate = now.toISOString().replace(/[:-]|\.\d{3}/g, '').slice(0, 15) + 'Z';
  const datestamp = amzdate.slice(0, 8);

  const ext = file.name.split('.').pop().toLowerCase();
  const s3Key = `${folder}/${Date.now()}.${ext}`;
  const contentType = file.type || 'application/octet-stream';

  const fileBuffer = await file.arrayBuffer();
  const contentHash = await sha256(new Uint8Array(fileBuffer));

  const credScope = `${datestamp}/${REGION}/s3/aws4_request`;
  const signedHdrs = 'content-type;host;x-amz-content-sha256;x-amz-date';
  const canonHdrs = `content-type:${contentType}\nhost:${HOST}\nx-amz-content-sha256:${contentHash}\nx-amz-date:${amzdate}\n`;
  const canonReq = `PUT\n/${BUCKET}/${s3Key}\n\n${canonHdrs}\n${signedHdrs}\n${contentHash}`;
  const sts = `AWS4-HMAC-SHA256\n${amzdate}\n${credScope}\n${await sha256(canonReq)}`;

  const sigKey = await getSigningKey(datestamp);
  const sig = toHex(await hmac(sigKey, sts));
  const auth = `AWS4-HMAC-SHA256 Credential=${ACCESS_KEY}/${credScope}, SignedHeaders=${signedHdrs}, Signature=${sig}`;

  const res = await fetch(`https://${HOST}/${BUCKET}/${s3Key}`, {
    method: 'PUT',
    headers: {
      'x-amz-date': amzdate,
      'x-amz-content-sha256': contentHash,
      'Content-Type': contentType,
      'Authorization': auth,
    },
    body: file,
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Garage upload failed: ${res.status} ${err}`);
  }

  return s3Key;
}
